import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from "@asm/ui";
import {
  BarChart3,
  RefreshCw,
  Play,
  Loader2,
  Download,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";
import {
  adminApi,
  type TldSummary,
  type FindingDistribution,
  type DailyActivity,
  type AnalyticsJob,
  type AnalyticsJobType,
} from "@/lib/api";

type SortField = "domain_count" | "avg_email_score" | "avg_headers_score" | "total_vulns";
type SortDirection = "asc" | "desc";

export function ReconAnalytics() {
  const [tldSummary, setTldSummary] = useState<TldSummary[]>([]);
  const [findingDist, setFindingDist] = useState<FindingDistribution[]>([]);
  const [dailyActivity, setDailyActivity] = useState<DailyActivity[]>([]);
  const [jobs, setJobs] = useState<AnalyticsJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // TLD table sorting
  const [sortField, setSortField] = useState<SortField>("domain_count");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  // Job launcher state
  const [jobType, setJobType] = useState<AnalyticsJobType>("tech_distribution");
  const [dateStart, setDateStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [dateEnd, setDateEnd] = useState(() => new Date().toISOString().split("T")[0]);
  const [launching, setLaunching] = useState(false);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const [tld, dist, activity, jobList] = await Promise.allSettled([
        adminApi.getTldSummary(),
        adminApi.getFindingDistribution(),
        adminApi.getDailyActivity(),
        adminApi.listAnalyticsJobs(),
      ]);

      if (tld.status === "fulfilled") setTldSummary(tld.value);
      if (dist.status === "fulfilled") setFindingDist(dist.value);
      if (activity.status === "fulfilled") setDailyActivity(activity.value);
      if (jobList.status === "fulfilled") setJobs(jobList.value.jobs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await adminApi.refreshAnalytics();
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  const handleLaunchJob = async () => {
    setLaunching(true);
    try {
      const job = await adminApi.createAnalyticsJob({
        job_type: jobType,
        date_start: dateStart,
        date_end: dateEnd,
      });
      setJobs((prev) => [job, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to launch job");
    } finally {
      setLaunching(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sortedTlds = [...tldSummary].sort((a, b) => {
    const va = a[sortField] ?? 0;
    const vb = b[sortField] ?? 0;
    return sortDir === "asc" ? (va > vb ? 1 : -1) : va < vb ? 1 : -1;
  });

  // Group findings by module for the distribution chart
  const findingsByModule = findingDist.reduce<Record<string, { total: number; bySeverity: Record<string, number> }>>(
    (acc, f) => {
      const mod = f.module ?? "unknown";
      if (!acc[mod]) acc[mod] = { total: 0, bySeverity: {} };
      acc[mod].total += f.count;
      const sev = f.severity ?? "unknown";
      acc[mod].bySeverity[sev] = (acc[mod].bySeverity[sev] ?? 0) + f.count;
      return acc;
    },
    {}
  );

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === "asc" ? " \u2191" : " \u2193") : "";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Recon Analytics</h2>
          <p className="text-sm text-muted-foreground">
            Aggregated intelligence from recon scan results
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh Views
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TLD Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            TLD Summary
          </CardTitle>
          <CardDescription>
            Security metrics aggregated by top-level domain ({tldSummary.length} TLDs)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">TLD</th>
                  <th
                    className="text-right py-2 px-3 font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("domain_count")}
                  >
                    Domains{sortIndicator("domain_count")}
                  </th>
                  <th
                    className="text-right py-2 px-3 font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("avg_email_score")}
                  >
                    Email Score{sortIndicator("avg_email_score")}
                  </th>
                  <th
                    className="text-right py-2 px-3 font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("avg_headers_score")}
                  >
                    Headers Score{sortIndicator("avg_headers_score")}
                  </th>
                  <th
                    className="text-right py-2 px-3 font-medium cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("total_vulns")}
                  >
                    Vulns{sortIndicator("total_vulns")}
                  </th>
                  <th className="text-right py-2 px-3 font-medium">Technologies</th>
                  <th className="text-right py-2 px-3 font-medium">Subdomains</th>
                </tr>
              </thead>
              <tbody>
                {sortedTlds.slice(0, 50).map((row) => (
                  <tr key={row.tld} className="border-b border-muted/50 hover:bg-muted/30">
                    <td className="py-2 px-3 font-mono">.{row.tld}</td>
                    <td className="text-right py-2 px-3">
                      {row.domain_count.toLocaleString()}
                      <span className="text-muted-foreground ml-1 text-xs">
                        ({row.scanned_count} scanned)
                      </span>
                    </td>
                    <td className="text-right py-2 px-3">
                      <ScoreBadge score={row.avg_email_score} />
                    </td>
                    <td className="text-right py-2 px-3">
                      <ScoreBadge score={row.avg_headers_score} />
                    </td>
                    <td className="text-right py-2 px-3">
                      {row.total_vulns > 0 ? (
                        <span className="text-destructive font-medium">
                          {row.total_vulns.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="text-right py-2 px-3">
                      {row.total_technologies.toLocaleString()}
                    </td>
                    <td className="text-right py-2 px-3">
                      {row.total_subdomains.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tldSummary.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No data available. Run some recon scans first.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Finding Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Finding Distribution (Last 30 Days)</CardTitle>
          <CardDescription>
            Finding types grouped by module and severity
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(findingsByModule)
              .sort(([, a], [, b]) => b.total - a.total)
              .map(([mod, data]) => (
                <div key={mod} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium capitalize">{mod}</span>
                    <span className="text-sm text-muted-foreground">
                      {data.total.toLocaleString()} findings
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {(["critical", "high", "medium", "low"] as const).map((sev) => {
                      const count = data.bySeverity[sev] || 0;
                      if (count === 0) return null;
                      return (
                        <Badge
                          key={sev}
                          variant={sev === "critical" || sev === "high" ? "destructive" : "secondary"}
                          className="text-xs"
                        >
                          {sev}: {count}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ))}
            {Object.keys(findingsByModule).length === 0 && (
              <p className="text-center text-muted-foreground py-4">
                No findings in the last 30 days.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Daily Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Scan Activity (Last 90 Days)</CardTitle>
          <CardDescription>
            Scan volume and metrics over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">Date</th>
                  <th className="text-right py-2 px-3 font-medium">Scans</th>
                  <th className="text-right py-2 px-3 font-medium">Partial</th>
                  <th className="text-right py-2 px-3 font-medium">Email Score</th>
                  <th className="text-right py-2 px-3 font-medium">Headers Score</th>
                  <th className="text-right py-2 px-3 font-medium">Vulns</th>
                  <th className="text-right py-2 px-3 font-medium">Subdomains</th>
                </tr>
              </thead>
              <tbody>
                {dailyActivity.slice(0, 30).map((row) => (
                  <tr
                    key={row.scan_date}
                    className="border-b border-muted/50 hover:bg-muted/30"
                  >
                    <td className="py-2 px-3 font-mono text-xs">{row.scan_date}</td>
                    <td className="text-right py-2 px-3">{row.scans.toLocaleString()}</td>
                    <td className="text-right py-2 px-3 text-muted-foreground">
                      {row.partial_scans}
                    </td>
                    <td className="text-right py-2 px-3">
                      <ScoreBadge score={row.avg_email_score} />
                    </td>
                    <td className="text-right py-2 px-3">
                      <ScoreBadge score={row.avg_headers_score} />
                    </td>
                    <td className="text-right py-2 px-3">{row.vulns}</td>
                    <td className="text-right py-2 px-3">{row.subdomains.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {dailyActivity.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No daily activity data available.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Analytics Job Launcher */}
      <Card>
        <CardHeader>
          <CardTitle>Deep Analytics Jobs (S3 MapReduce)</CardTitle>
          <CardDescription>
            Run batch analysis over full recon results stored in S3.
            These jobs process raw data for deeper insights.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Job Type</label>
              <select
                className="rounded-md border bg-background px-3 py-2 text-sm"
                value={jobType}
                onChange={(e) => setJobType(e.target.value as AnalyticsJobType)}
              >
                <option value="tech_distribution">Technology Distribution</option>
                <option value="cloud_analysis">Cloud Provider Analysis</option>
                <option value="security_scores">Security Score Distribution</option>
                <option value="vuln_prevalence">Vulnerability Prevalence</option>
                <option value="subdomain_patterns">Subdomain Patterns</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Start Date</label>
              <input
                type="date"
                className="rounded-md border bg-background px-3 py-2 text-sm"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">End Date</label>
              <input
                type="date"
                className="rounded-md border bg-background px-3 py-2 text-sm"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
              />
            </div>
            <Button onClick={handleLaunchJob} disabled={launching}>
              {launching ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Launch Job
            </Button>
          </div>

          {/* Jobs List */}
          {jobs.length > 0 && (
            <div className="mt-6 space-y-3">
              <h4 className="text-sm font-medium">Recent Jobs</h4>
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <JobStatusIcon status={job.status} />
                    <div>
                      <span className="text-sm font-medium capitalize">
                        {job.job_type.replace(/_/g, " ")}
                      </span>
                      <p className="text-xs text-muted-foreground">
                        {job.date_start} to {job.date_end}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={
                        job.status === "completed"
                          ? "default"
                          : job.status === "failed"
                          ? "destructive"
                          : "secondary"
                      }
                    >
                      {job.status}
                    </Badge>
                    {job.status === "completed" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={downloadingJobId === job.id}
                        onClick={async () => {
                          setDownloadingJobId(job.id);
                          try {
                            const fullJob = await adminApi.getAnalyticsJob(job.id);
                            const blob = new Blob(
                              [JSON.stringify(fullJob.result, null, 2)],
                              { type: "application/json" }
                            );
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${job.job_type}_${job.date_start}_${job.date_end}.json`;
                            a.click();
                            URL.revokeObjectURL(url);
                          } catch {
                            setError("Failed to download job result");
                          } finally {
                            setDownloadingJobId(null);
                          }
                        }}
                      >
                        {downloadingJobId === job.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    {job.error && (
                      <span className="text-xs text-destructive max-w-48 truncate">
                        {job.error}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Helper components

function ScoreBadge({ score }: { score: number }) {
  const rounded = Math.round(score);
  const color =
    rounded >= 80
      ? "text-green-500"
      : rounded >= 50
      ? "text-yellow-500"
      : "text-red-500";

  return <span className={`font-medium ${color}`}>{rounded}</span>;
}

function JobStatusIcon({ status }: { status: string }) {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case "failed":
      return <XCircle className="h-5 w-5 text-destructive" />;
    case "running":
      return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
    default:
      return <Clock className="h-5 w-5 text-muted-foreground" />;
  }
}
